import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AuthenticationStateHelperService } from '../../_helpers/authentication-state-helper/authentication-state-helper.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { ColumnDef, DEFAULT_PAGE_SIZE, PageChange, SortChange, SortDirection } from '../../shared/data-table/data-table.model';
import { DynamicConfirmModalComponent } from '../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { PagedResponse } from '../../_models/common/paged-response.model';
import { UserMaintenanceService } from '../../_services/user-maintenance/user-maintenance.service';
import { AuthMethod, LastSignInFilter, MaintenanceRole, UserListItem, UserMaintenanceSearchRequest } from '../../_models/admin/user-maintenance.models';
import { BrandFilterService } from '../../_services/brand-filter.service';
import { BrandInfo } from '../../_models/reports/brand-info.model';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FilterSpec, FilterState } from '../../shared/filter-ux/filter-state';
import { ScreenFilterUx } from '../../shared/filter-ux/screen-filter-ux';
import { AccessCheckService } from '../../_services/access-check/access-check.service';
import { AccessCheckUserSuggestion } from '../../_models/admin/access-check.models';
import { hasRole } from '../../_helpers/can-use/current-user-roles';

/**
 * List of ALL users (internal + external) with server-side paging/sorting and per-row maintenance actions.
 * Self-gates on the UserMaintenanceView feature; action controls are additionally gated on UserMaintenanceEdit.
 * Only ADMIN sees the MFA toggle and the role filter, and someone without ADMIN gets no actions on an ADMIN
 * account. These only hide controls: the API enforces each of them.
 */
@Component({
  selector: 'user-maintenance',
  templateUrl: 'user-maintenance.page.html',
  standalone: false
})
export class UserMaintenancePage implements OnInit, OnDestroy {

  public canEdit: boolean = false;
  /** The signed-in user holds ADMIN: MFA, roles and ADMIN accounts are theirs alone. */
  public isAdmin: boolean = false;

  public columns: ColumnDef<UserListItem>[] = [];

  private readonly baseColumns: ColumnDef<UserListItem>[] = [
    { field: 'status', header: 'Status', type: 'template', width: '10%' },
    { field: 'name', header: 'Name', type: 'template' },
    { field: 'email', header: 'Email', type: 'text', sortable: true },
    { field: 'mobileNumber', header: 'Mobile', type: 'text', width: '12%' },
    { field: 'type', header: 'Type', type: 'template', width: '8%' },
    { field: 'roles', header: 'Roles', type: 'text' },
    { field: 'auth', header: 'Sign-in', type: 'template', width: '10%' },
    { field: 'mfa', header: 'MFA', type: 'template', width: '8%' },
    { field: 'action', header: '', type: 'template', width: '18%' }
  ];

  public rows: UserListItem[] = [];
  public totalCount: number = 0;
  public pageSize: number = DEFAULT_PAGE_SIZE;
  public pageNumber: number = 1;
  public sortField: string = null;
  public sortDir: SortDirection = 'asc';
  public loading: boolean = false;
  public errorMsg: string = '';

  public searchTerm: string = '';
  public includeDeleted: boolean = false;

  /** Empty string is "any" — the request sends null rather than an empty filter. */
  public authMethod: AuthMethod | '' = '';

  /**
   * The filters.user-maintenance switch. Off, the screen is exactly as it was before the filter work:
   * no brand filter, no Brand column, no role/status/type filters, none of their request parameters.
   */
  public readonly filtersKey = FeatureSwitchKeys.filtersUserMaintenance;
  public filtersEnabled: boolean = false;

  /** Selected brands; empty means every brand the user may see. */
  public companyIds: number[] = [];
  /** The user's brands; the Brand column shows only when there is more than one. */
  public brands: BrandInfo[] = [];
  /** Role code filter; empty string is any role. */
  public roleCode: string = '';
  /** 'true' / 'false' from the select; empty string is either. */
  public activeFilter: string = '';
  /** 'true' (internal) / 'false' (external); empty string is either. */
  public internalFilter: string = '';
  /** Roles for the role filter. */
  public roles: MaintenanceRole[] = [];
  /** Last sign-in filter; empty string is any. */
  public lastSignInFilter: LastSignInFilter = '';

  /**
   * search.autocomplete.user-maintenance: the search box suggests matching accounts (api/Maintenance/AccessCheck/Users,
   * which answers 404 while admin.access-check is off, so then there are none). Off, it is a plain box.
   */
  public autocompleteEnabled: boolean = false;
  public readonly searchUserSuggestions = (q: string) => this.accessCheck.searchUsers(q);
  public readonly suggestionUserName = (s: AccessCheckUserSuggestion): string => s?.userName ?? '';
  public readonly suggestionDetail = (s: AccessCheckUserSuggestion): string => [s?.name, s?.email].filter(x => !!x).join(' · ');
  /** A pick puts the email in the box (the search matches first name, last name or email), else the user name. */
  public readonly suggestionSearchText = (s: AccessCheckUserSuggestion): string => s?.email || s?.userName || '';

  /**
   * Query-string names for the filters, which are also what is remembered. Ids, codes, presets and dates
   * only: free text, names and emails stay out of the URL (browser history, telemetry) and browser storage.
   */
  private static readonly filterSpec: FilterSpec = {
    signIn: 'string', deleted: 'boolean', brand: 'number[]', role: 'string', status: 'string', type: 'string'
  };
  /** Remembered filters and filter state in the URL; used only while filters.user-maintenance is on. */
  private filterUx: ScreenFilterUx;

  /** Requests are funnelled through switchMap so a rapid page/sort click cannot land out of order. */
  private searchRequested: Subject<UserMaintenanceSearchRequest> = new Subject<UserMaintenanceSearchRequest>();
  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public router: Router,
    private featureHelperService: FeatureHelperService,
    private authenticationStateHelper: AuthenticationStateHelperService,
    private service: UserMaintenanceService,
    private dialog: MatDialog,
    private successModalHelper: SuccessModalHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private brandFilterService: BrandFilterService,
    private featureSwitches: FeatureSwitchService,
    @Optional() private route: ActivatedRoute = null,
    @Optional() private accessCheck: AccessCheckService = null
  ) { }

  ngOnInit(): void {
    this.buildColumns();

    const perms = this.featureHelperService.checkFeaturePermissions(['UserMaintenanceView', 'UserMaintenanceEdit']);

    const viewPerm = perms.find(f => f.feature === 'UserMaintenanceView');
    if (!viewPerm || !viewPerm.enabled) {
      console.error('User does not have the required permissions to view the user maintenance screen');
      this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.pathname } });
      this.authenticationStateHelper.unauthorizedToken = true;
      return;
    }

    const editPerm = perms.find(f => f.feature === 'UserMaintenanceEdit');
    this.canEdit = !!editPerm && editPerm.enabled;
    this.isAdmin = hasRole('ADMIN');

    this.searchRequested.pipe(
      switchMap((request: UserMaintenanceSearchRequest) => {
        this.loading = true;
        return this.service.searchUsers(request);
      }),
      takeUntil(this.unsubscribe)
    ).subscribe({
      next: (response: PagedResponse<UserListItem>) => {
        this.loading = false;
        this.errorMsg = '';
        this.totalCount = response.totalCount;
        this.rows = response.items;
        if (response.items.length === 0) {
          this.errorMsg = 'No data found';
        }
      },
      error: error => {
        this.loading = false;
        this.errorModalHelper.show(error.message, error);
      }
    });

    this.filterUx = new ScreenFilterUx(this.filtersKey, UserMaintenancePage.filterSpec, this.router, this.route);

    this.featureSwitches.isEnabled(FeatureSwitchKeys.searchAutocompleteUserMaintenance).pipe(takeUntil(this.unsubscribe))
      .subscribe(enabled => this.autocompleteEnabled = enabled);
    this.featureSwitches.isEnabled(this.filtersKey).pipe(takeUntil(this.unsubscribe)).subscribe(enabled => {
      this.filtersEnabled = enabled;
      this.buildColumns();
      if (!enabled) {
        return;
      }
      // A shared link's filters, else this user's last ones. Applied once the switch is known to be on,
      // so with the switch off nothing is read.
      const initial = this.filterUx.initialState();
      if (initial) {
        this.applyFilterState(initial);
        this.search();
      }
      this.brandFilterService.getMyBrands().pipe(takeUntil(this.unsubscribe)).subscribe(brands => {
        this.brands = brands ?? [];
        this.buildColumns();
      });
      // The role list is ADMIN only on the API; anyone else filters without it.
      if (this.isAdmin) {
        this.service.getRoles().pipe(takeUntil(this.unsubscribe)).subscribe(roles => this.roles = roles ?? []);
      }
    });

    this.search();
  }

  /** The user's brand is `applicationId` on these rows. The column is part of the switched filter UI. */
  private buildColumns(): void {
    const brandColumn: ColumnDef<UserListItem>[] = this.filtersEnabled && this.brands.length > 1
      ? [{ field: 'applicationId', header: 'Brand', type: 'template', width: '8%' }]
      : [];
    // Last sign-in sits after the Sign-in (method) column; part of the switched filter UI.
    const lastSignInColumn: ColumnDef<UserListItem>[] = this.filtersEnabled
      ? [{ field: 'lastSignInUtc', header: 'Last sign-in', type: 'template', width: '11%', sortable: true }]
      : [];
    const authIndex = this.baseColumns.findIndex(c => c.field === 'auth') + 1;
    this.columns = [
      this.baseColumns[0],
      ...brandColumn,
      ...this.baseColumns.slice(1, authIndex),
      ...lastSignInColumn,
      ...this.baseColumns.slice(authIndex)
    ];
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public search(): void {
    this.pageNumber = 1;
    if (this.filtersEnabled) {
      this.filterUx.save(this.filterState());
    }
    this.runSearch();
  }

  /** Clear all: back to the screen's defaults, forgotten, and out of the URL. */
  public clearAll(): void {
    this.searchTerm = '';
    this.applyFilterState({});
    this.filterUx.clear();
    this.pageNumber = 1;
    this.runSearch();
  }

  private filterState(): FilterState {
    return {
      signIn: this.authMethod,
      deleted: this.includeDeleted || null,
      brand: this.companyIds,
      role: this.roleCode,
      status: this.activeFilter,
      type: this.internalFilter
    };
  }

  private applyFilterState(state: FilterState): void {
    this.authMethod = ((state.signIn as string) ?? '') as AuthMethod | '';
    this.includeDeleted = state.deleted === true;
    this.companyIds = (state.brand as number[]) ?? [];
    this.roleCode = (state.role as string) ?? '';
    this.activeFilter = (state.status as string) ?? '';
    this.internalFilter = (state.type as string) ?? '';
  }

  private buildRequest(): UserMaintenanceSearchRequest {
    const request: UserMaintenanceSearchRequest = {
      searchParam: this.searchTerm,
      includeDeleted: this.includeDeleted,
      authMethod: this.authMethod || null,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortDirection: this.sortDir
    };
    // The switched filters travel only when filters.user-maintenance is on.
    if (this.filtersEnabled) {
      request.companyIds = this.companyIds;
      request.roleCode = this.roleCode || null;
      request.active = this.activeFilter === '' ? null : this.activeFilter === 'true';
      request.isInternal = this.internalFilter === '' ? null : this.internalFilter === 'true';
      Object.assign(request, UserMaintenancePage.lastSignInParams(this.lastSignInFilter));
    }
    return request;
  }

  /** Maps the one Last sign-in select onto the API's three narrow-only fields. */
  public static lastSignInParams(filter: LastSignInFilter): Pick<UserMaintenanceSearchRequest, 'signedInWithinDays' | 'notSignedInForDays' | 'neverSignedIn'> {
    const params: Pick<UserMaintenanceSearchRequest, 'signedInWithinDays' | 'notSignedInForDays' | 'neverSignedIn'> =
      { signedInWithinDays: null, notSignedInForDays: null, neverSignedIn: null };
    if (filter === 'never') {
      params.neverSignedIn = true;
    } else if (filter) {
      const [kind, days] = filter.split(':');
      if (kind === 'within') {
        params.signedInWithinDays = Number(days);
      } else if (kind === 'over') {
        params.notSignedInForDays = Number(days);
      }
    }
    return params;
  }

  private runSearch(): void {
    this.searchRequested.next(this.buildRequest());
  }

  public onPageChange(event: PageChange): void {
    this.pageNumber = event.page;
    this.pageSize = event.pageSize;
    this.runSearch();
  }

  public onSortChange(event: SortChange): void {
    this.sortField = event.field;
    this.sortDir = event.direction;
  }

  /** The row's actions are offered: the user may edit, and the row is not an ADMIN account unless they are ADMIN. */
  public canManage(user: UserListItem): boolean {
    if (!this.canEdit) {
      return false;
    }
    const rowIsAdmin = (user?.roles ?? '').split(',').some(r => r.trim().toUpperCase() === 'ADMIN');
    return this.isAdmin || !rowIsAdmin;
  }

  public editUser(user: UserListItem): void {
    this.router.navigate(['/user-maintenance/edit', user.id]);
  }

  public toggleActive(user: UserListItem): void {
    this.service.setActive(user.id, !user.active).subscribe({
      next: () => {
        this.successModalHelper.show('User has been updated');
        this.runSearch();
      },
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  public deleteUser(user: UserListItem): void {
    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: 'Delete user ' + user.firstName + ' ' + user.lastName + '?' }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.service.deleteUser(user.id).subscribe({
          next: () => {
            this.successModalHelper.show('User has been deleted');
            this.runSearch();
          },
          error: error => this.errorModalHelper.show(error.message, error)
        });
      });
  }

  public resendActivation(user: UserListItem): void {
    this.service.resendActivation(user.id).subscribe({
      next: () => this.successModalHelper.show('New activation code has been sent'),
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  public sendPasswordReset(user: UserListItem): void {
    this.service.sendPasswordReset(user.email, user.applicationId).subscribe({
      next: () => this.successModalHelper.show('Password reset link has been sent'),
      error: error => this.errorModalHelper.show(error.message, error)
    });
  }

  public toggleMfa(user: UserListItem, checked: boolean): void {
    this.service.setMfaRequired(user.id, checked).subscribe({
      next: () => { user.mfaRequired = checked; },
      error: error => this.errorModalHelper.show('Could not update MFA. Please try again.', error)
    });
  }
}
